import { Component, OnInit, Input, ViewChild, Output, EventEmitter, ChangeDetectorRef } from '@angular/core';
import { FlatTreeControl } from '@angular/cdk/tree';
import { MatTreeFlattener, MatTreeFlatDataSource, MatTree } from '@angular/material/tree';
import { TreeControlNode } from '../../model/tree-control-node';
import { TreeNode } from '../../model/tree-node';
import { TreeFormatter } from '../../util/tree-formatter';
import { Nameable } from 'src/app/common/model/nameable';

@Component({
  selector: 'app-expandable-tree',
  templateUrl: './expandable-tree.component.html',
  styleUrls: ['./expandable-tree.component.scss']
})
export class ExpandableTreeComponent<T extends Nameable> implements OnInit {

  @ViewChild('tree') tree: MatTree<T>;

  @Input()
  data?: T[];
  @Input()
  title: string;
  @Input()
  startExpanded: boolean = false;
  @Input()
  treeFormatter: TreeFormatter<T>;
  @Input()
  searchEnabled: boolean = true;
  @Input()
  grouping: string[];
  @Output()
  elementClick = new EventEmitter<T>();

  treeControl: FlatTreeControl<TreeControlNode<T>>;
  treeFlattener: MatTreeFlattener<TreeNode<T>, TreeControlNode<T>>;
  dataSource: MatTreeFlatDataSource<TreeNode<T>, TreeControlNode<T>>;
  private expandedNodes: Array<any>;
  visibleElementsCount: number;

  filterValue: string = '';
  selectedGroup: string = null;

  constructor(private changeDetector: ChangeDetectorRef) {
    this.treeControl = new FlatTreeControl<TreeControlNode<T>>(node => node.level, node => node.expandable);
    this.treeFlattener = new MatTreeFlattener(this.transformer, node => node.level, node => node.expandable, node => node.children);
    this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
  }

  ngOnInit(): void {
    if (this.data != null) {
      this.sortData();
      this.createDataSource();
    }
  }

  private transformer = (node: TreeNode<T>, level: number): { expandable: boolean; name: string; elements: number; level: number; data: T } => ({
      expandable: !!node.children && node.children.length > 0,
      name: node.title || this.treeFormatter.nodeTitleFormatter(node.data),
      elements: node.children ? node.children.length : 0,
      level,
      data: node.data
    });

  hasChild = (_: number, node: TreeControlNode<T>): boolean => node.expandable;

  updateData(newData: T[]): void {
    if (this.data == null)
      this.data = [];
    this.treeFormatter.updateData(this.data, newData);
    this.sortData();
    this.createDataSource();
  }

  private sortData(): void {
    this.data = this.data.sort((first, second) =>
      (this.treeFormatter.nodeTitleFormatter(first) > this.treeFormatter.nodeTitleFormatter(second) ? 1 : -1)
    );
  }

  applyFilter(filterValue: string = ''): void {
    this.filterValue = filterValue;
    this.createDataSource();
  }

  clearSearchInput(): void {
    this.applyFilter();
  }

  groupChanged(): void {
    this.createDataSource();
  }

  private filterData(): T[] {
    if (this.filterValue.length > 0) {
      const filter = this.filterValue.toLowerCase();
      return this.data.filter(element => this.treeFormatter.nodeTitleFormatter(element).toLowerCase().indexOf(filter) > -1);
    } else {
      return this.data;
    }
  }

  private createDataSource(): void {
    const filteredData = this.filterData();
    this.visibleElementsCount = filteredData.length;
    if (this.selectedGroup) {
      this.saveExpandedNodes();
      this.dataSource.data = this.treeFormatter.groupedDataCreator(filteredData, this.selectedGroup);
      this.restoreExpandedNodes();
    } else {
      this.dataSource.data = this.treeFormatter.dataCreator(filteredData);
    }
    this.changeDetector.detectChanges();
  }

  saveExpandedNodes(): void {
    this.expandedNodes = new Array<any>();
    this.treeControl.dataNodes.forEach(node => {
      if (node.expandable && this.treeControl.isExpanded(node)) {
        this.expandedNodes.push(node);
      }
    });
  }

  restoreExpandedNodes(): void {
    this.expandedNodes.forEach(node => {
      this.treeControl.expand(this.treeControl.dataNodes.find(n => n.name === node.name));
    });
  }

}
